/** Entry collection-move transport and response contract. */
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import { getAgentSessionId, kernelMutation } from "../client.js";
import { failureResult, notFoundResult, success } from "../envelope.js";

export const moveEntrySchema = z.object({
  entryId: z.string().describe("Entry ID to move, e.g. '<PREFIX>-<n>'"),
  toCollection: z.string().describe("Target collection slug, e.g. 'decisions', 'architecture'"),
});

export async function handleMoveEntry(entryId: string, toCollection: string) {
  try {
    const sessionId = getAgentSessionId();
    const result = await kernelMutation<GatewayRouteReturnByName<"chain.moveToCollection">>("chain.moveToCollection", {
      entryId,
      toCollectionSlug: toCollection,
      changedBy: sessionId ? `agent:${sessionId}` : undefined,
    });
    if (result.outcome === "refused") {
      const recovery = result.refusal.requiredAction ?? "Ask the record owner or relevant Team Lead for the required authority change.";
      return failureResult(
        `Move refused for **${entryId}**.\n\n${result.refusal.reason}\n\n${recovery}`,
        "AUTHORITY_REFUSED",
        result.refusal.reason,
        recovery,
        undefined,
        { entryId, toCollection, refusal: result.refusal },
      );
    }

    const lines = [
      `## Move Result`,
      "",
      `Moved **${result.entryId}** from \`${result.fromCollection}\` → \`${result.toCollection}\``,
    ];
    if (result.workflowStatusReset) {
      lines.push(``, `⚠️ Workflow status \`${result.previousWorkflowStatus}\` was reset (incompatible with target collection).`);
    }

    return {
      content: [{ type: "text" as const, text: lines.join("\n") }],
      structuredContent: success(
        `Moved ${result.entryId} from ${result.fromCollection} to ${result.toCollection}.`,
        result,
        [
          { tool: "entries", description: "View updated entry", parameters: { action: "get", entryId } },
          { tool: "quality", description: "Re-check quality", parameters: { action: "check", entryId } },
        ],
      ),
    };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes("not found")) {
      return notFoundResult(entryId, `Could not move entry: ${msg}`);
    }
    return failureResult(
      `Could not move **${entryId}** to \`${toCollection}\`: ${msg}`,
      "MOVE_FAILED",
      msg,
      "Check the entry ID and target collection slug, then retry.",
      [{ tool: "entries", description: "Search for entry", parameters: { action: "search", query: entryId } }],
    );
  }
}
